<template>
  <div class="bg-white w-full p-28 max-[1024px]:p-[4rem_1.25rem_2rem_1.25rem] font-rubik">
    <div class="mx-auto max-w-[64rem] flex flex-col gap-16 max-[1024px]:gap-8">
      <div class="flex flex-col gap-4">
        <h5 class="text-[1.5rem] leading-[2.125rem]">Which container is right for you?</h5>
        <p class="text-[1rem] leading-[1.5rem]">Compare conditions and grades</p>
      </div>
      <div class="flex gap-12">
        <template v-if="windowWidth > 950">
          <div
            class="flex flex-col gap-3"
          >
            <button
              v-for="(container, key) in containerStats"
              :key="key"
              class="py-4 px-5 rounded-lg h-[2.75rem] flex items-center text-[#33415599] uppercase text-[0.875rem] leading-[0.75rem] font-semibold text-left text-nowrap"
              :class="[
                { '!bg-[#E0DAD1] !text-[#334155]': key === selectedContainerStatsKey }
              ]"
              @click="selectContainerStats(key)"
            >
              {{ container.title }}
            </button>
          </div>
          <ContainerStatsCard
            :container-stats="selectedContainerStats"
          />
        </template>
        <template v-else>
          <div class="flex flex-col gap-5 w-full">
            <div class="horizontal-scroll pb-1">
              <ContainerStatsCard
                v-for="(container, key) in containerStats"
                :key="key"
                :container-stats="container"
              />
            </div>
            <a 
              href="/solutions/container-sales"
              class="bg-[#3565FC] !text-white py-[0.625rem] px-[1.25rem] rounded-full flex items-center justify-center"
            >
              Explore Products
            </a>
          </div>
        </template>
      </div>
      <div class="flex flex-col gap-4">
        <h5 class="text-[1.5rem] leading-[2.125rem]">What size is my container?</h5>
        <p class="text-[1rem] leading-[1.5rem]">Container Dimensions</p>
      </div>
      <template v-if="windowWidth > 950">
        <ContainerDimensionsTable :container-types-sizes="containerTypesSizes"/>
        <ContainerCapacityTable :container-types-sizes="containerTypesSizes"/>
      </template>
      <template v-else>
        <div class="flex flex-col gap-1">
          <span class="text-[0.875rem] leading-[1.25rem]">Pick a container size</span>
          <div
            class="relative p-3 border border-[#EDE9E3] bg-[#F9F8F6] cursor-pointer rounded-lg flex justify-between items-center select-none"
            @click.stop="toggleContainerTypesSizesDropdown"
          >
            <span class="font-medium text-[1rem] leading-[1.5rem]">
              {{ selectedContainerTypeSizeName }}
            </span>
            <span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7.64683 9.18828L8 9.54068L8.35317 9.18828L11.0596 6.48773L11.2929 6.72101L8 10.0139L4.70711 6.72101L4.94039 6.48773L7.64683 9.18828Z" fill="#334155" stroke="#334155"/>
              </svg>
            </span>
            <div
              v-if="isContainerTypesSizesDropdownOpen"
              class="absolute w-full left-0 top-[calc(100%+0.25rem)] flex flex-col bg-white border border-[#EDE9E3] rounded-lg max-h-[23rem] overflow-y-auto z-40"
              v-click-outside="() => { isContainerTypesSizesDropdownOpen = false }"
            >
              <span
                v-for="container in containerTypesSizes"
                :key="container.name"
                class="px-5 py-2 text-[0.875rem] leading-[1.25rem] hover:bg-[#EDE9E3]"
                :class="[
                  { '!bg-[#334155] text-white': container.name === selectedContainerTypeSizeName }
                ]"
                @click="selectContainerTypeSize(container.name)"
              >
                {{ container.name }}
              </span>
            </div>
          </div>
        </div>
        <SplitContainerTypeSizeTablesView :container-type-size="selectedContainerTypeSize"/>
      </template>
    </div>
  </div>
</template>
<script>
import ClickOutside from 'vue-click-outside'
import ContainerStatsCard from '../container-stats-card/index.vue'
import ContainerDimensionsTable from '../container-dimensions-table/index.vue'
import ContainerCapacityTable from '../container-capacity-table/index.vue'
import SplitContainerTypeSizeTablesView from '../split-container-type-size-tables-view/index.vue'
export default {
  name: 'WhichContainerIsRightForYou',
  directives: {
    ClickOutside
  },
  components: {
    ContainerStatsCard,
    ContainerDimensionsTable,
    ContainerCapacityTable,
    SplitContainerTypeSizeTablesView
  },
  props: {
    oneTripContainerOptionImageUrl: {
      type: String,
      default: ''
    },
    refurbishedContainerOptionImageUrl: {
      type: String,
      default: ''
    },
    iiclContainerOptionImageUrl: {
      type: String,
      default: ''
    },
    cargoWorthyContainerOptionImageUrl: {
      type: String,
      default: ''
    },
    windAndWaterTightContainerOptionImageUrl: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      windowWidth: window.innerWidth,
      selectedContainerStatsKey: 'oneTrip',
      containerStats: {
        oneTrip: {
          title: 'One Trip Container',
          image: this.oneTripContainerOptionImageUrl,
          imageAlt: 'One Trip Container',
          longevity: 100,
          condition: 100,
          aesthetics: 100,
          cost: 100
        },
        refurbished: {
          title: 'Refurbished Container',
          image: this.refurbishedContainerOptionImageUrl,
          imageAlt: 'Refurbished Container',
          longevity: 80,
          condition: 80,
          aesthetics: 80,
          cost: 80
        },
        iicl: {
          title: 'IICL Container',
          image: this.iiclContainerOptionImageUrl,
          imageAlt: 'IICL Container',
          longevity: 60,
          condition: 80,
          aesthetics: 60,
          cost: 80
        },
        cargoWorthy: {
          title: 'Cargo Worthy Container',
          image: this.cargoWorthyContainerOptionImageUrl,
          imageAlt: 'Cargo Worthy Container',
          longevity: 60,
          condition: 60,
          aesthetics: 60,
          cost: 60
        },
        windAndWaterTight: {
          title: 'Wind & Water Tight Container',
          image: this.windAndWaterTightContainerOptionImageUrl,
          imageAlt: 'Wind & Water Tight Container',
          longevity: 40,
          condition: 40,
          aesthetics: 40,
          cost: 20
        }
      },
      selectedContainerTypeSizeName: '20\' Standard Dry',
      isContainerTypesSizesDropdownOpen: false,
      containerTypesSizes: [
        {
          name: '10\' Standard Dry',
          exterior: {
            length: '10\'',
            width: '8\'',
            height: '8\'6"'
          },
          interior: {
            length: '9\'3"',
            width: '7\'8"',
            height: '7\'9⅞"'
          },
          doorOpening: {
            width: '7\'8"',
            height: '7\'5"'
          },
          cubicCapacity: null,
          liquidCubicCapacity: null,
          tareWeight: null,
          maxGross: null,
          maxPayload: null
        },
        {
          name: '20\' Standard Dry',
          exterior: {
            length: '20\'',
            width: '8\'',
            height: '8\'6"'
          },
          interior: {
            length: '19\'3"',
            width: '7\'8"',
            height: '7\'9⅞"'
          },
          doorOpening: {
            width: '7\'8"',
            height: '7\'5"'
          },
          cubicCapacity: '1,165 cu. ft.',
          liquidCubicCapacity: null,
          tareWeight: '5,050 lbs',
          maxGross: '67,200 lbs',
          maxPayload: '62,150 lbs'
        },
        {
          name: '40\' Standard Dry',
          exterior: {
            length: '40\'',
            width: '8\'',
            height: '8\'6"'
          },
          interior: {
            length: '39\'5"',
            width: '7\'8"',
            height: '7\'9⅞"'
          },
          doorOpening: {
            width: '7\'8"',
            height: '7\'5"'
          },
          cubicCapacity: '2,350 cu. ft.',
          liquidCubicCapacity: null,
          tareWeight: '8,000 lbs',
          maxGross: '67,200 lbs',
          maxPayload: '59,200 lbs'
        },
        {
          name: '40\' High Cube Dry',
          exterior: {
            length: '40\'',
            width: '8\'',
            height: '9\'6"'
          },
          interior: {
            length: '39\'5"',
            width: '7\'8"',
            height: '8\'10"'
          },
          doorOpening: {
            width: '7\'8"',
            height: '8\'5½"'
          },
          cubicCapacity: '2,694 cu. ft.',
          liquidCubicCapacity: null,
          tareWeight: '8,775 lbs',
          maxGross: '67,200 lbs',
          maxPayload: '58,425 lbs'
        },
        {
          name: '45\' High Cube Dry',
          exterior: {
            length: '45\'',
            width: '8\'',
            height: '9\'6"'
          },
          interior: {
            length: '44\'5"',
            width: '7\'8"',
            height: '8\'10"'
          },
          doorOpening: {
            width: '7\'8"',
            height: '8\'5½"'
          },
          cubicCapacity: '3,043 cu. ft.',
          liquidCubicCapacity: null,
          tareWeight: '9,810 lbs',
          maxGross: '72,800 lbs',
          maxPayload: '62,990 lbs'
        },
        {
          name: '20\' Refrigerated',
          exterior: {
            length: '19\'10½"',
            width: '8\'',
            height: '8\'6"'
          },
          interior: {
            length: '17\'11"',
            width: '7\'6"',
            height: '7\'8"'
          },
          doorOpening: {
            width: '7\'6"',
            height: '7\'3"'
          },
          cubicCapacity: '1,010 cu. ft.',
          liquidCubicCapacity: '967',
          tareWeight: '6,503 lbs',
          maxGross: '52,800 lbs',
          maxPayload: '46,407 lbs'
        },
        {
          name: '40\' Refrigerated',
          exterior: {
            length: '40\'',
            width: '8\'',
            height: '8\'6"'
          },
          interior: {
            length: '37\'11"',
            width: '7\'6"',
            height: '7\'8"'
          },
          doorOpening: {
            width: '7\'6"',
            height: '7\'6"'
          },
          cubicCapacity: '2,051 cu. ft.',
          liquidCubicCapacity: '1,959',
          tareWeight: '9,700 lbs',
          maxGross: '67,200 lbs',
          maxPayload: '57,450 lbs'
        },
        {
          name: '40\' High Cube Refrigerated',
          exterior: {
            length: '40\'',
            width: '8\'',
            height: '9\'6"'
          },
          interior: {
            length: '37\'11"',
            width: '7\'6"',
            height: '8\'4"'
          },
          doorOpening: {
            width: '7\'6"',
            height: '8\'4"'
          },
          cubicCapacity: '2,380 cu. ft.',
          liquidCubicCapacity: '2,289',
          tareWeight: '9,590 lbs',
          maxGross: '70,550 lbs',
          maxPayload: '60,960 lbs'
        }
      ]
    }
  },
  computed: {
    selectedContainerStats() {
      return this.containerStats[this.selectedContainerStatsKey]
    },
    selectedContainerTypeSize() {
      return this.containerTypesSizes.find(container => container.name === this.selectedContainerTypeSizeName)
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth
    },
    selectContainerStats(key) {
      if (this.selectedContainerStatsKey === key) {
        return
      }

      this.selectedContainerStatsKey = key
    },
    selectContainerTypeSize(name) {
      this.selectedContainerTypeSizeName = name
    },
    toggleContainerTypesSizesDropdown() {
      this.isContainerTypesSizesDropdownOpen = !this.isContainerTypesSizesDropdownOpen
    }
  }
}
</script>
<style lang="scss" scoped>
.horizontal-scroll {
  overflow-x: auto;
  max-width: 100%;
  touch-action: pan-x;
  display: flex;
  gap: 12px;

  scrollbar-width: thin;
  scrollbar-color: #888;
  &:hover {
    scrollbar-color: #555;
  }

  /* width */
  &::-webkit-scrollbar {
    height: 4px;
  }

  /* Track */
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  /* Handle */
  &::-webkit-scrollbar-thumb {
    background: #888;
  }

  /* Handle on hover */
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
}
</style>